import { supabase } from '@/lib/supabase'
import { Company } from '@/types'

function isDataImageUrl(value: string | undefined): boolean {
  if (!value) return false
  return /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(value.trim())
}

function isLegacyDemoCompanyRecord(company: any): boolean {
  if (!company) return false

  const id = String(company.id || '').toLowerCase().trim()
  const name = String(company.name || '').toLowerCase().trim()
  const email = String(company.email || '').toLowerCase().trim()

  if (id.startsWith('demo_company_')) return true
  if (name.includes('(demo)')) return true
  if (name === 'demo company oü' || name === 'techstart oü') return true
  if (email.endsWith('@democompany.ee') || email.endsWith('@techstart.ee')) return true

  return false
}

export interface CompanyWithStatus extends Company {
  status: 'active' | 'inactive' | 'archived'
  is_favorite: boolean
}

export interface CreateCompanyData {
  name: string
  registration_code?: string
  vat_code?: string
  address?: string
  city?: string
  zip_code?: string
  country?: string
  email?: string
  phone?: string
  logo_url?: string
  invoice_type?: string
  is_test?: boolean
  bank_accounts?: { id: string; name: string; bic_swift: string; iban: string }[]
}

export interface UpdateCompanyData extends Partial<CreateCompanyData> {
  status?: 'active' | 'inactive' | 'archived'
  is_favorite?: boolean
  is_active?: boolean
  is_test?: boolean
}

export class CompanyService {
  /**
   * Get all companies for the current user
   */
  static async getUserCompanies(userId: string): Promise<CompanyWithStatus[]> {
    try {
      const { data, error } = await supabase
        .from('user_companies')
        .select(`
          *,
          company:companies(*)
        `)
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        const errorMessageRaw = typeof error.message === 'string' ? error.message : ''
        const errorMessage = errorMessageRaw.trim()
        const errorCode = typeof error.code === 'string' ? error.code.trim() : ''

        // Avoid noisy dev overlays from effectively empty errors (e.g. "{}" / "[object Object]").
        const isEffectivelyEmptyMessage =
          !errorMessage ||
          errorMessage === '{}' ||
          errorMessage === '[object Object]'

        if (isEffectivelyEmptyMessage && !errorCode) {
          console.warn('User companies fetch returned an empty error object, using fallback data source')
          return []
        }

        // If this is likely a table not found error, return empty array for non-mock users
        if (errorMessage.includes('Could not find the table') || errorCode === 'PGRST116') {
          console.warn('Database tables not found, returning empty array')
          return []
        }

        // Non-fatal fallback: avoid console.error to prevent Next.js error overlay for recoverable fetch failures.
        console.warn('User companies fetch failed, using fallback data source', {
          code: errorCode || 'unknown',
          message: errorMessage || 'unknown',
        })
        return []
      }

      if (!data || data.length === 0) {
        return []
      }

      // Transform the data to match our interface
      return data.map(item => ({
        ...item.company,
        // These properties are handled through the user_companies relationship
        // We'll set defaults for now since the actual status might be in user_companies
        status: 'active' as const,
        is_favorite: false
      })) as CompanyWithStatus[]

    } catch (error) {
      console.error('Error in getUserCompanies:', error)
      throw error
    }
  }

  /**
   * Mock companies for demo users - check localStorage first
   */
  static getMockCompanies(): CompanyWithStatus[] {
    try {
      // Check localStorage first for updated company data
      const companiesKey = 'companies'
      const existingCompanies = JSON.parse(localStorage.getItem(companiesKey) || '[]')
      
      console.log('🔍 CompanyService: Checking localStorage for companies:', existingCompanies.length)
      
      // If we have companies in localStorage, use those (they have the real uploaded logos)
      if (existingCompanies.length > 0) {
        console.log('🔍 CompanyService: Using companies from localStorage with uploaded logos')
        return existingCompanies
          .filter((company: any) => !isLegacyDemoCompanyRecord(company))
          .map((company: any) => ({
          id: company.id,
          name: company.name,
          registration_code: company.registration_code || '',
          vat_code: company.vat_code || '',
          address: company.address || '',
          city: company.city || '',
          zip_code: company.zip_code || '',
          country: company.country || 'Estonia',
          email: company.email || '',
          phone: company.phone || '',
          logo_url: company.logo_url || '', // This will have the real uploaded logo URL
          bank_accounts: Array.isArray(company.bank_accounts) ? company.bank_accounts : [],
          invoice_type: company.invoice_type || 'standard',
          status: company.status || 'active',
          is_favorite: company.is_favorite || false,
          created_at: company.created_at || new Date().toISOString(),
          updated_at: company.updated_at || new Date().toISOString()
        }))
      }

      // Do not inject synthetic demo companies automatically.
      console.log('🔍 CompanyService: No companies found in localStorage, returning empty list')
      return []
    } catch (error) {
      console.error('Error getting mock companies:', error)
      // Return empty array on error
      return []
    }
  }

  /**
   * Get a single company by ID (only if user has access)
   */
  static async getCompanyById(companyId: string, userId: string): Promise<CompanyWithStatus | null> {
    try {
      const { data, error } = await supabase
        .from('user_companies')
        .select(`
          *,
          company:companies(*)
        `)
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // No access to this company
        }
        console.error('Error fetching company:', error)
        throw new Error(error.message)
      }

      if (!data) {
        return null
      }

      return {
        ...data.company,
        status: 'active' as const,
        is_favorite: false
      } as CompanyWithStatus

    } catch (error) {
      console.error('Error in getCompanyById:', error)
      throw error
    }
  }

  /**
   * Create a new company
   */
  static async createCompany(companyData: CreateCompanyData, userId: string): Promise<CompanyWithStatus> {
    try {
      if (isDataImageUrl(companyData.logo_url)) {
        throw new Error('Base64 logo URLs are not allowed. Upload logo to storage first.')
      }

      // Start a transaction-like operation
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          ...companyData,
          country: companyData.country || 'Estonia',
          invoice_type: companyData.invoice_type || 'netgaming',
          status: 'active'
        })
        .select()
        .single()

      if (companyError) {
        console.error('Error creating company:', companyError)
        throw new Error(companyError.message)
      }

      // Create user-company relationship
      const { error: relationError } = await supabase
        .from('user_companies')
        .insert({
          user_id: userId,
          company_id: company.id,
          role: 'owner',
          is_active: true,
        })

      if (relationError) {
        console.error('Error creating user-company relationship:', relationError)
        // Try to clean up the company if relationship creation fails
        await supabase.from('companies').delete().eq('id', company.id)
        throw new Error(relationError.message)
      }

      return {
        ...company,
        status: 'active' as const,
        is_favorite: false
      } as CompanyWithStatus

    } catch (error) {
      console.error('Error in createCompany:', error)
      throw error
    }
  }

  /**
   * Update company information
   */
  static async updateCompany(companyId: string, updateData: UpdateCompanyData, userId: string): Promise<CompanyWithStatus> {
    try {
      if (isDataImageUrl(updateData.logo_url)) {
        throw new Error('Base64 logo URLs are not allowed. Upload logo to storage first.')
      }

      // First verify user has access to this company
      const hasAccess = await this.checkCompanyAccess(companyId, userId)
      if (!hasAccess) {
        throw new Error('Access denied to this company')
      }

      const { data, error } = await supabase
        .from('companies')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId)
        .select()
        .single()

      if (error) {
        console.error('Error updating company:', error)
        throw new Error(error.message)
      }

      return {
        ...data,
        status: 'active' as const,
        is_favorite: false
      } as CompanyWithStatus

    } catch (error) {
      console.error('Error in updateCompany:', error)
      throw error
    }
  }

  /**
   * Update company status (active/inactive/archived)
   */
  static async updateCompanyStatus(companyId: string, status: 'active' | 'inactive' | 'archived', userId: string): Promise<CompanyWithStatus> {
    return this.updateCompany(companyId, { status }, userId)
  }

  /**
   * Toggle company favorite status
   */
  static async toggleCompanyFavorite(companyId: string, isFavorite: boolean, userId: string): Promise<CompanyWithStatus> {
    return this.updateCompany(companyId, { is_favorite: isFavorite }, userId)
  }

  /**
   * Archive a company
   */
  static async archiveCompany(companyId: string, userId: string): Promise<CompanyWithStatus> {
    return this.updateCompany(companyId, { status: 'archived' }, userId)
  }

  /**
   * Delete a company (only owner can delete)
   */
  static async deleteCompany(companyId: string, userId: string): Promise<void> {
    try {
      // First verify user is owner of this company
      const { data: relation, error: relationError } = await supabase
        .from('user_companies')
        .select('role')
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .single()

      if (relationError || !relation) {
        throw new Error('Company not found or access denied')
      }

      if (relation.role !== 'owner') {
        throw new Error('Only company owners can delete companies')
      }

      // Delete the user-company relationship
      const { error: deleteRelationError } = await supabase
        .from('user_companies')
        .delete()
        .eq('company_id', companyId)

      if (deleteRelationError) {
        throw new Error(deleteRelationError.message)
      }

      // Delete the company
      const { error: deleteCompanyError } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId)

      if (deleteCompanyError) {
        throw new Error(deleteCompanyError.message)
      }

    } catch (error) {
      console.error('Error in deleteCompany:', error)
      throw error
    }
  }

  /**
   * Check if user has access to a company
   */
  static async checkCompanyAccess(companyId: string, userId: string): Promise<boolean> {
    try {
      // For mock users, always return true (they have access to all mock companies)
      if (userId === 'user_demo_001' || userId === 'user_demo_002' || userId === 'user_mock_001') {
        return true
      }

      const { data, error } = await supabase
        .from('user_companies')
        .select('id')
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single()

      return !error && !!data
    } catch (error) {
      return false
    }
  }

  /**
   * Get companies filtered by status
   */
  static async getCompaniesByStatus(status: 'active' | 'inactive' | 'archived', userId: string): Promise<CompanyWithStatus[]> {
    const allCompanies = await this.getUserCompanies(userId)
    return allCompanies.filter(company => company.status === status)
  }

  /**
   * Get favorite companies
   */
  static async getFavoriteCompanies(userId: string): Promise<CompanyWithStatus[]> {
    const allCompanies = await this.getUserCompanies(userId)
    return allCompanies.filter(company => company.is_favorite)
  }

  /**
   * Get active companies only
   */
  static async getActiveCompanies(userId: string): Promise<CompanyWithStatus[]> {
    const allCompanies = await this.getUserCompanies(userId)
    return allCompanies.filter(company => company.status === 'active')
  }
}
